import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';
import { formatExpiryForInput } from '@/lib/payments/paymentAccount.format';
import { makeEditCardSchema } from '@/lib/payments/paymentAccount.schema';
import type { PaymentAccountFormValues } from '@/lib/payments/paymentAccount.types';

interface UseEditCardFormArgs {
  card: CreditCardAccount;
  /** Whether the billing sub-form must be filled (member opened "Change"). */
  billingRequired: boolean;
  /** Injectable clock for deterministic expiry bounds in tests. */
  now?: Date;
}

/**
 * Seeds the shared `PaymentAccountFormValues` shape from a saved card so the existing
 * card/billing field components (fields.tsx, BillingInformationSection) work unchanged — the
 * `card.cardNumber`/`cvv` slots are unused placeholders (the PAN is immutable and not rendered
 * on Edit). `card.autoPay` seeds `false`; `AutoPayCheckbox` forces it to the real enrollment
 * status once `useMemberAutoPaySummary` resolves, same as the Add modal. Billing seeds
 * from the card's OWN saved address, not the member profile — Edit shows per-payment-method
 * billing, matching legacy.
 */
export function seedEditCardFormValues(card: CreditCardAccount): PaymentAccountFormValues {
  return {
    card: {
      cardNumber: '',
      expiry: formatExpiryForInput(card.expirationMonth, card.expirationYear),
      cvv: '',
      isHsa: card.isHsa,
      makeDefault: card.isDefault,
      autoPay: false,
    },
    bank: {
      routingNumber: '',
      accountNumber: '',
      bankName: '',
      accountType: 'checking',
      isHsa: false,
      makeDefault: true,
      autoPay: true,
    },
    billing: {
      name: card.holderName,
      line1: card.line1,
      line2: card.line2,
      city: card.city,
      state: card.state,
      zip: card.zip,
    },
  };
}

/** The RHF instance for the Edit Payment Account modal — credit card. */
export function useEditCardForm({ card, billingRequired, now }: UseEditCardFormArgs) {
  const argsRef = useRef({ billingRequired, now });
  argsRef.current = { billingRequired, now };

  const resolver = useMemo<Resolver<PaymentAccountFormValues>>(
    () => (values, context, options) => {
      const { billingRequired, now } = argsRef.current;
      const schema = makeEditCardSchema({ billingRequired, now });
      return (zodResolver(schema) as unknown as Resolver<PaymentAccountFormValues>)(values, context, options);
    },
    [],
  );

  // `useForm`'s `defaultValues` only take effect at initial mount — the orchestrator is expected
  // to key/remount the form per edit session (mirrors AddPaymentAccountModal's reset-on-open),
  // so this memo doesn't need to guard against re-seeding over an in-progress edit.
  const defaultValues = useMemo(() => seedEditCardFormValues(card), [card]);

  return useForm<PaymentAccountFormValues>({ resolver, defaultValues, mode: 'onTouched' });
}
